import React, { useState } from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Volume2, Play, Check, VolumeX, Mic, Sparkles, Bell } from 'lucide-react';
import {
  TONES, getSelectedTone, setSelectedTone, playTone,
  isGreetingEnabled, setGreetingEnabled, playGreetingSample, buildGreetingText, getTodayFestival,
  AI_VOICES, getSelectedVoice, setSelectedVoice, speakGreeting,
  getVolume, setVolume, playNotificationAlert,
  getCustomGreeting, setCustomGreeting
} from '../utils/welcomeTones';
import { toast } from 'sonner';

const VOLUME_ROWS = [
  { kind: 'tone', label: 'Welcome Tone', icon: Volume2 },
  { kind: 'greeting', label: 'Voice Greeting', icon: Mic },
  { kind: 'notification', label: 'Notification Alert', icon: Bell },
];

export const SoundSettings = () => {
  const [selected, setSelected] = useState(getSelectedTone());
  const [greetOn, setGreetOn] = useState(isGreetingEnabled());
  const [voice, setVoice] = useState(getSelectedVoice());
  const [previewing, setPreviewing] = useState(null);
  const [customText, setCustomText] = useState(getCustomGreeting());
  const [volumes, setVolumes] = useState({
    tone: getVolume('tone'),
    greeting: getVolume('greeting'),
    notification: getVolume('notification'),
  });
  const userName = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').full_name || ''; } catch { return ''; }
  })();
  const festival = getTodayFestival();

  const choose = (id) => {
    setSelected(id);
    setSelectedTone(id);
    if (id !== 'off') playTone(id);
    toast.success(id === 'off' ? 'Welcome tone turned off' : `Welcome tone set: ${TONES.find(t => t.id === id)?.name}`);
  };

  const toggleGreeting = (on) => {
    setGreetOn(on);
    setGreetingEnabled(on);
    toast.success(on ? 'Voice greeting on' : 'Voice greeting off');
  };

  const chooseVoice = async (id) => {
    setVoice(id);
    setSelectedVoice(id);
    setPreviewing(id);
    try { await speakGreeting(userName, id); } finally { setPreviewing(null); }
  };

  const changeVolume = (kind, value) => {
    setVolumes((v) => ({ ...v, [kind]: value }));
    setVolume(kind, value);
  };

  const previewVolume = (kind) => {
    if (kind === 'tone') playTone(selected === 'off' ? 'gentle_chime' : selected);
    else if (kind === 'notification') playNotificationAlert();
    else speakGreeting(userName);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative touch-target" title="Welcome tone settings" data-testid="sound-settings-btn">
          {selected === 'off' ? <VolumeX className="w-5 h-5 text-slate-400" /> : <Volume2 className="w-5 h-5 text-slate-600" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3" data-testid="sound-settings-popover">
        <h4 className="text-sm font-semibold text-slate-800 mb-1">Welcome Tone</h4>
        <p className="text-xs text-slate-500 mb-3">Plays a soft tone when you open the app. Tap ▶ to hear a sample.</p>
        <div className="space-y-1">
          {TONES.map(t => (
            <div key={t.id}
              className={`flex items-center justify-between rounded-lg px-2 py-1.5 cursor-pointer transition-colors ${selected === t.id ? 'bg-orange-50 border border-orange-200' : 'hover:bg-slate-50 border border-transparent'}`}
              onClick={() => choose(t.id)}
              data-testid={`tone-option-${t.id}`}>
              <div className="flex items-center gap-2">
                <span className={`w-4 ${selected === t.id ? 'text-orange-600' : 'text-transparent'}`}><Check className="w-4 h-4" /></span>
                <div>
                  <p className="text-sm font-medium text-slate-800">{t.name}</p>
                  <p className="text-[10px] text-slate-400">{t.description}</p>
                </div>
              </div>
              <button type="button"
                onClick={(e) => { e.stopPropagation(); playTone(t.id); }}
                className="p-1.5 rounded-full text-slate-500 hover:bg-orange-100 hover:text-orange-600"
                title="Play sample" data-testid={`tone-play-${t.id}`}>
                <Play className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <div
            className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer transition-colors ${selected === 'off' ? 'bg-slate-100 border border-slate-300' : 'hover:bg-slate-50 border border-transparent'}`}
            onClick={() => choose('off')}
            data-testid="tone-option-off">
            <span className={`w-4 ${selected === 'off' ? 'text-slate-600' : 'text-transparent'}`}><Check className="w-4 h-4" /></span>
            <div className="flex items-center gap-2">
              <VolumeX className="w-4 h-4 text-slate-400" />
              <p className="text-sm font-medium text-slate-600">Off — no welcome tone</p>
            </div>
          </div>
        </div>

        {/* Voice greeting */}
        <div className="mt-3 pt-3 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-orange-500" />
              <div>
                <p className="text-sm font-semibold text-slate-800">Voice Greeting <span className="text-[9px] font-bold text-orange-500 bg-orange-100 px-1 py-0.5 rounded ml-1">AI VOICE</span></p>
                <p className="text-[10px] text-slate-400">Studio-quality AI voice welcomes you by name</p>
              </div>
            </div>
            <Switch checked={greetOn} onCheckedChange={toggleGreeting} data-testid="greeting-toggle" />
          </div>
          {greetOn && (
            <div className="grid grid-cols-2 gap-1.5 mt-2" data-testid="voice-choices">
              {AI_VOICES.map(v => (
                <button key={v.id} type="button" onClick={() => chooseVoice(v.id)}
                  className={`text-left rounded-lg px-2 py-1.5 border transition-colors ${voice === v.id ? 'bg-orange-50 border-orange-300' : 'border-slate-200 hover:bg-slate-50'}`}
                  data-testid={`voice-option-${v.id}`}>
                  <p className="text-xs font-semibold text-slate-800 flex items-center gap-1">
                    {v.name}
                    {voice === v.id && <Check className="w-3 h-3 text-orange-600" />}
                    {previewing === v.id && <span className="text-[9px] text-orange-500 animate-pulse">playing…</span>}
                  </p>
                  <p className="text-[9px] text-slate-400">{v.tag}</p>
                </button>
              ))}
            </div>
          )}
          {greetOn && (
            <div className="mt-2" data-testid="custom-greeting-section">
              <Label className="text-xs font-medium text-slate-700">Custom Greeting Message</Label>
              <Textarea
                value={customText}
                onChange={(e) => { setCustomText(e.target.value); setCustomGreeting(e.target.value); }}
                placeholder={'e.g. Namaste {name}, wishing you a productive day at JMEPL!'}
                className="mt-1 text-xs min-h-[52px]"
                maxLength={200}
                data-testid="custom-greeting-input"
              />
              <div className="flex items-center justify-between mt-1 gap-2">
                <p className="text-[10px] text-slate-400">{'{name}'} = your name. Festival wishes are added automatically.</p>
                {customText && (
                  <button type="button" className="text-[10px] text-orange-600 hover:underline whitespace-nowrap"
                    onClick={() => { setCustomText(''); setCustomGreeting(''); }} data-testid="custom-greeting-reset">
                    Reset to default
                  </button>
                )}
              </div>
            </div>
          )}
          <p className="text-xs text-slate-500 italic mt-2 bg-slate-50 rounded-lg p-2" data-testid="greeting-preview-text">
            “{buildGreetingText(userName)}”
          </p>
          {festival && (
            <p className="text-xs text-amber-700 mt-1.5 flex items-center gap-1" data-testid="festival-today">
              <Sparkles className="w-3 h-3" /> Today's festival: <strong>{festival}</strong> — included in the greeting
            </p>
          )}
          <Button size="sm" className="w-full mt-2 h-8 bg-orange-500 hover:bg-orange-600 text-xs"
            onClick={() => playGreetingSample(userName)} data-testid="greeting-sample-btn">
            <Play className="w-3 h-3 mr-1.5" /> Hear Full Welcome Sample
          </Button>
        </div>

        {/* Volume controls */}
        <div className="mt-3 pt-3 border-t" data-testid="volume-controls">
          <h4 className="text-sm font-semibold text-slate-800 mb-2">Volume</h4>
          <div className="space-y-3">
            {VOLUME_ROWS.map(({ kind, label, icon: Icon }) => (
              <div key={kind} data-testid={`volume-row-${kind}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    {volumes[kind] === 0 ? <VolumeX className="w-3.5 h-3.5 text-slate-400" /> : <Icon className="w-3.5 h-3.5 text-slate-500" />}
                    <span className="text-xs font-medium text-slate-700">{label}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-semibold w-8 text-right">{volumes[kind] === 0 ? 'Mute' : `${volumes[kind]}%`}</span>
                </div>
                <Slider
                  value={[volumes[kind]]}
                  min={0} max={100} step={5}
                  onValueChange={([v]) => changeVolume(kind, v)}
                  onValueCommit={() => previewVolume(kind)}
                  data-testid={`volume-slider-${kind}`}
                />
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-2">Release a slider to hear a sample at that volume.</p>
        </div>
      </PopoverContent>
    </Popover>
  );
};
